<script setup lang="ts">
import SlideOver from "@/components/SlideOver.vue";
import StyledInput from "@/components/StyledInput.vue";
import StyledSelect from "@/components/StyledSelect.vue";

interface Props {
  when: boolean;
  title?: string;
  icon?: string;
  iconBgColor?: string;
  iconColor?: string;
  buttonColor?: string;
  filters: {
    q?: string;
    orderBy?: string;
    orderDirection?: string;
    active?: number | string;
  };
}

interface Emits {
  (e: "close"): void;
  (e: "search", value: string): void;
  (e: "update:orderBy", value: string): void;
  (e: "update:orderDirection", value: string): void;
  (e: "update:active", value: number | undefined): void;
  (e: "reset"): void;
}

const props = withDefaults(defineProps<Props>(), {
  title: "Filtros",
  icon: "filter_alt",
  iconBgColor: "bg-blue-100",
  iconColor: "text-blue-600",
  buttonColor: "bg-blue-600 hover:bg-blue-700",
});

const emits = defineEmits<Emits>();

const orderByOptions = [
  { value: "name", label: "Nombre" },
  { value: "created_at", label: "Fecha de creación" },
];

const orderDirectionOptions = [
  { value: "asc", label: "Ascendente" },
  { value: "desc", label: "Descendente" },
];

const statusOptions = [
  { value: undefined, label: "Todos" },
  { value: 1, label: "Activos" },
  { value: 0, label: "Inactivos" },
];

const handleReset = () => {
  emits("reset");
};
</script>

<template>
  <SlideOver
    :when="props.when"
    :title="props.title"
    @update:close-slide-over="emits('close')"
    @update:save-slide-over="emits('close')"
    :hide-buttons="true"
  >
    <template #default>
      <div class="flex h-full flex-col bg-gray-50">
        <!-- Header -->
        <div class="border-b border-gray-200 bg-white px-5 py-4">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <div :class="['flex h-10 w-10 items-center justify-center rounded-xl', props.iconBgColor]">
                <span :class="['material-symbols-outlined text-xl', props.iconColor]">{{ props.icon }}</span>
              </div>
              <div>
                <h2 class="text-lg font-semibold text-gray-900">Filtros</h2>
                <p class="text-xs text-gray-500">Personaliza tu búsqueda</p>
              </div>
            </div>
            <button
              @click="handleReset"
              v-tooltip="'Limpiar filtros'"
              class="flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-700 transition-colors hover:bg-amber-100"
            >
              <span class="material-symbols-outlined text-lg">mop</span>
              Limpiar
            </button>
          </div>
        </div>

        <!-- Filter sections -->
        <div class="flex-1 overflow-y-auto p-5 space-y-6">
          <!-- Search section -->
          <div class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div class="mb-3 flex items-center gap-2">
              <span class="material-symbols-outlined text-lg text-gray-400">search</span>
              <h3 class="text-sm font-semibold text-gray-700">Búsqueda</h3>
            </div>
            <StyledInput
              :model-value="props.filters.q"
              label="Nombre"
              placeholder="Escribe para buscar..."
              @input="(e: string) => emits('search', e)"
            />
          </div>

          <!-- Status section -->
          <div class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div class="mb-3 flex items-center gap-2">
              <span class="material-symbols-outlined text-lg text-gray-400">toggle_on</span>
              <h3 class="text-sm font-semibold text-gray-700">Estatus</h3>
            </div>
            <StyledSelect
              :model-value="props.filters.active"
              @update:model-value="(v) => emits('update:active', v)"
              :options="statusOptions"
              label="Mostrar registros"
              placeholder="Seleccionar estado"
            />
          </div>

          <!-- Order section -->
          <div class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div class="mb-3 flex items-center gap-2">
              <span class="material-symbols-outlined text-lg text-gray-400">sort</span>
              <h3 class="text-sm font-semibold text-gray-700">Ordenamiento</h3>
            </div>
            <div class="grid grid-cols-2 gap-3">
              <StyledSelect
                :model-value="props.filters.orderBy"
                @update:model-value="(v) => emits('update:orderBy', v)"
                :options="orderByOptions"
                label="Ordenar por"
              />
              <StyledSelect
                :model-value="props.filters.orderDirection"
                @update:model-value="(v) => emits('update:orderDirection', v)"
                :options="orderDirectionOptions"
                label="Dirección"
              />
            </div>
          </div>

          <!-- Extra filters slot -->
          <slot name="extraFilters" />
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex items-center gap-3 border-t border-gray-200 bg-white p-4">
        <button
          type="button"
          class="flex flex-1 items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
          @click="handleReset"
        >
          <span class="material-symbols-outlined text-lg">mop</span>
          Limpiar
        </button>
        <button
          type="button"
          :class="[
            'flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors',
            props.buttonColor,
          ]"
          @click="emits('close')"
        >
          <span class="material-symbols-outlined text-lg">check</span>
          Aplicar filtros
        </button>
      </div>
    </template>
  </SlideOver>
</template>
